import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import adminApi from '@/lib/adminApi';
import { ServerCrash, MonitorX } from 'lucide-react';

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : '-');

export default function AdminMonitoringPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await adminApi.getMonitoringErrors();
      setData(r.data);
      setError('');
    } catch {
      setError('Failed to load monitoring data');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout title="Monitoring">
      <div className="space-y-6" data-testid="admin-monitoring-page">
        {error && <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm" data-testid="monitoring-error">{error}</div>}

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Backend error groups', value: data?.backend_total ?? '-', testid: 'backend-total' },
            { label: 'Client errors (all time)', value: data?.client_total ?? '-', testid: 'client-total' },
            { label: 'Client errors (24h)', value: data?.client_24h ?? '-', testid: 'client-24h' },
          ].map(c => (
            <div key={c.label} className="bg-white rounded-lg border border-[#E8ECF0] p-4" data-testid={c.testid}>
              <p className="text-xs text-[#4E6974]">{c.label}</p>
              <p className="text-2xl font-bold text-[#0D3B4F] mt-1">{c.value}</p>
            </div>
          ))}
        </div>

        {/* Backend errors */}
        <div className="bg-white rounded-lg border border-[#E8ECF0] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E8ECF0] flex items-center gap-2">
            <ServerCrash className="h-4 w-4 text-[#FF3B30]" />
            <h2 className="text-sm font-bold text-[#0D3B4F]">Backend errors (grouped)</h2>
          </div>
          {!data?.backend_errors?.length ? (
            <p className="px-4 py-8 text-sm text-[#4E6974] text-center" data-testid="backend-errors-empty">No backend errors recorded</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F8FAFB] text-left text-xs text-[#4E6974]">
                  <tr>
                    <th className="px-4 py-2">Endpoint</th>
                    <th className="px-4 py-2">Type</th>
                    <th className="px-4 py-2">Message</th>
                    <th className="px-4 py-2">Count</th>
                    <th className="px-4 py-2">Last seen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0F2F5]">
                  {data.backend_errors.map(e => (
                    <tr key={e.fingerprint} data-testid="backend-error-row">
                      <td className="px-4 py-2 font-mono text-xs text-[#0D3B4F] whitespace-nowrap">{e.method} {e.path}</td>
                      <td className="px-4 py-2 text-[#FF3B30] text-xs">{e.error_type}</td>
                      <td className="px-4 py-2 text-[#4E6974] max-w-[300px] truncate">{e.message}</td>
                      <td className="px-4 py-2 font-semibold text-[#0D3B4F]">{e.count}</td>
                      <td className="px-4 py-2 text-xs text-[#4E6974] whitespace-nowrap">{fmtTime(e.last_seen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Client errors */}
        <div className="bg-white rounded-lg border border-[#E8ECF0] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E8ECF0] flex items-center gap-2">
            <MonitorX className="h-4 w-4 text-[#FF9500]" />
            <h2 className="text-sm font-bold text-[#0D3B4F]">Client errors (latest 50)</h2>
          </div>
          {!data?.client_errors?.length ? (
            <p className="px-4 py-8 text-sm text-[#4E6974] text-center" data-testid="client-errors-empty">No client errors reported</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F8FAFB] text-left text-xs text-[#4E6974]">
                  <tr>
                    <th className="px-4 py-2">Message</th>
                    <th className="px-4 py-2">Page</th>
                    <th className="px-4 py-2">Source</th>
                    <th className="px-4 py-2">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0F2F5]">
                  {data.client_errors.map(e => (
                    <tr key={e.id} data-testid="client-error-row">
                      <td className="px-4 py-2 text-[#0D3B4F] max-w-[320px] truncate">{e.message}</td>
                      <td className="px-4 py-2 text-xs text-[#4E6974] max-w-[200px] truncate">{e.url}</td>
                      <td className="px-4 py-2 text-xs text-[#4E6974]">{e.source}</td>
                      <td className="px-4 py-2 text-xs text-[#4E6974] whitespace-nowrap">{fmtTime(e.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
